import { FC, useEffect, useState } from 'react';
import parse from 'html-react-parser';

import CustomLoading from 'modules/_shared/components/CustomLoading';
import { getDocumentReplacedWithTags } from 'modules/documents/api/replaceDocumentTags';
import { useTranslate } from 'hooks/useTranslate';
import { store } from 'redux/store';

export type IncentiveOfferOperationPurchaseContractPreviewProps = {
  templateId: string;
  societyId: string;
  operationId: string;
  language: string;
};

const IncentiveOfferOperationPurchaseContractPreview: FC<
  IncentiveOfferOperationPurchaseContractPreviewProps
> = ({ templateId, societyId, operationId, language }) => {
  const { t } = useTranslate();
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!templateId || !societyId || !operationId) {
        setIsError(true);
        setIsLoading(false);
        setPreviewHtml(null);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const decoded = (await store.dispatch(
          getDocumentReplacedWithTags({
            documentId: templateId,
            sources: {
              society: societyId,
              incentiveOfferOperation: operationId,
            },
            language: language?.split('-')[0] || 'es',
          })
        )) as { html?: string } | undefined;

        if (cancelled) return;

        const rawHtml = decoded?.html;
        if (typeof rawHtml !== 'string' || !rawHtml.trim()) {
          setIsError(true);
          setPreviewHtml(null);
          return;
        }

        setPreviewHtml(rawHtml);
        setIsError(false);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setPreviewHtml(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [templateId, societyId, operationId, language]);

  if (isLoading) {
    return <CustomLoading />;
  }

  if (isError || !previewHtml) {
    return (
      <p className="text-soft mb-0 px-3 py-2">
        {t('ErrorNoIncentivesMarketTemplate')}
      </p>
    );
  }

  return (
    <div className="card card-bordered">
      <div className="card-inner p-4 p-md-5">
        <div
          className="avoid-default-font"
          style={{ maxHeight: '70vh', overflow: 'auto' }}
          data-testid="operation-purchase-contract-preview"
        >
          {parse(previewHtml)}
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationPurchaseContractPreview;
